import { parallel } from "@pagedip/util-promise";
import split from "split2";
import stream from "stream";
import through from "through2";
import * as child_process from "mz/child_process";

export async function git(cmd: string, args?: string[] | Record<string, any>, options?: child_process.ExecFileOptions) {
  const [stdout] = await child_process.execFile("git", [cmd, ...dargs(args)], {
    ...options,
    encoding: "utf8",
  });

  return stdout.trim();
}

export async function gitBranch(cwd?: string) {
  try {
    return await git("symbolic-ref", { short: true, _: ["HEAD"] }, { cwd });
  } catch (err) {
    return;
  }
}

export async function gitHead(cwd?: string) {
  return await git("rev-parse", ["HEAD"], { cwd });
}

export interface Commit {
  hash: string;
  subject: string;
  body: string;
  changes?: string[];
}

const diffTreeOptions = {
  commitId: false,
  nameOnly: true,
  r: true,
};

export async function getCommits({
  gitRef,
  cwd,
  filter,
  cache,
}: {
  gitRef?: string;
  cwd?: string;
  filter?: string;
  cache?: Map<string, Commit>;
}) {
  const commits: Commit[] = [];
  const fetch = gitRawCommits({ from: gitRef, cwd });
  fetch.setEncoding("utf-8");

  await new Promise((ok, bad) => {
    fetch.on("end", ok);
    fetch.on("error", bad);

    fetch.on("data", function (c: string) {
      try {
        const nli = c.trim().match(/^COMMIT\s([a-f0-9]+)\r?\n((?:.|\r?\n)*)$/);
        if (nli == null) return;

        const hash = nli[1];
        let commit = cache && cache.get(hash);

        if (!commit) {
          const [subject, ...bodyparts] = nli[2].split(/\r?\n/);

          commit = {
            hash,
            subject,
            body: bodyparts.join("\n").trim(),
          };

          if (cache) cache.set(hash, commit);
        }

        commits.push(commit);
      } catch (e) {
        bad(e);
      }
    });
  });

  await parallel(
    commits,
    async (commit) => {
      if (commit.changes) return;
      const res = await git("diff-tree", { ...diffTreeOptions, _: [commit.hash] }, { cwd });
      commit.changes = res.split("\n");
    },
    8
  );

  return filter ? filterCommitsToDirectory(commits, filter) : commits;
}

async function parseGitRef(
  ref: string,
  options?: {
    cwd?: string;
    cache?: Map<string, string | undefined>;
  }
) {
  options = options || {};

  if (options.cache && options.cache.has(ref)) {
    return options.cache.get(ref);
  }

  try {
    const result = await git("rev-parse", [`${ref}^{commit}`], { cwd: options.cwd });
    if (options.cache) options.cache.set(ref, result);
    return result;
  } catch (error) {
    if (typeof error.code !== "number") throw error;
    if (options.cache) options.cache.set(ref, undefined);
  }
}

export async function resolveGitRef(
  refs: string[],
  options?: {
    cwd?: string;
    cache?: Map<string, string | undefined>;
  }
) {
  for (const ref of refs) {
    const hash = await parseGitRef(ref, options);
    if (hash != null) return hash;
  }
}

function filterCommitsToDirectory(commits: Commit[], dir: string) {
  const dirparts = dir.split("/");

  return commits.filter((commit) => {
    return (
      commit.changes &&
      commit.changes.some((chg) => {
        const chgparts = chg.split("/");
        return dirparts.every((d, i) => d === chgparts[i]);
      })
    );
  });
}

function gitRawCommits(options?: { format?: string; from?: string; to?: string; cwd?: string }) {
  const readable = new stream.Readable();
  readable._read = function () {};

  options = options || {};
  const format = options.format || "COMMIT %H%n%B";
  const from = options.from || "";
  const to = options.to || "HEAD";

  const splitter = "------------------------ >8 ------------------------";
  const args = ["log", `--format=${format}%n${splitter}`, from ? [from, to].join("..") : to];

  let isError = false;
  const child = child_process.spawn("git", args, {
    // maxBuffer: Infinity,
    cwd: options.cwd,
  });

  if (child.stdout) {
    child.stdout.pipe(split(`${splitter}\n`)).pipe(
      through(
        function (chunk, enc, cb) {
          readable.push(chunk);
          isError = false;

          cb();
        },
        function (cb) {
          setImmediate(function () {
            if (!isError) {
              readable.push(null);
              readable.emit("close");
            }

            cb();
          });
        }
      )
    );
  }

  if (child.stderr) {
    child.stderr.pipe(
      through.obj(function (chunk) {
        isError = true;
        readable.emit("error", new Error(chunk));
        readable.emit("close");
      })
    );
  }

  return readable;
}

export function dargs(input?: Record<string, any> | string[]) {
  if (input == null) input = {};
  if (Array.isArray(input)) input = { _: input };

  const args: string[] = [];
  let extraArgs: string[] = [];
  let separatedArgs: string[] = [];

  const pushArg = (key: string, val?: string) => {
    key = key.replace(/[A-Z]/g, "-$&").toLowerCase();
    key = "-" + (key.length !== 1 ? "-" : "") + key;
    args.push(key + (val ? `=${val}` : ""));
  };

  for (const key of Object.keys(input) as string[]) {
    const val = input[key];

    if (key === "--") {
      if (!Array.isArray(val)) {
        throw new TypeError(`Expected key \`--\` to be Array, got ${typeof val}`);
      }

      separatedArgs = val;
      continue;
    }

    if (key === "_") {
      if (!Array.isArray(val)) {
        throw new TypeError(`Expected key \`_\` to be Array, got ${typeof val}`);
      }

      extraArgs = val;
      continue;
    }

    if (val === true) pushArg(key);
    else if (val === false) pushArg(`no-${key}`);
    else if (typeof val === "string") pushArg(key, val);
    else if (typeof val === "number" && !Number.isNaN(val)) pushArg(key, String(val));
    else if (Array.isArray(val)) {
      for (const arrayValue of val as any[]) {
        pushArg(key, arrayValue);
      }
    }
  }

  for (const x of extraArgs) {
    args.push(String(x));
  }

  if (separatedArgs.length > 0) {
    args.push("--");
  }

  for (const x of separatedArgs) {
    args.push(String(x));
  }

  return args;
}
